import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { Nullable, UserWithToken, WithData, WithMeta } from './api.types';

export type DecklistMatchConfidence = 'HIGH' | 'MEDIUM' | 'LOW';

export type TournamentDecklistCardEntry = { code: number; name: string; quantity: number };

export type TournamentDecklistIssues = {
  overLimit: { konamiId: number; copies: number; allowed: number }[];
  unknownKonamiIds: number[];
  noBanlist?: true;
  /** Genesys: puntos de la lista (tope 100). */
  genesysPoints?: number;
  /** Genesys: se pasa de 100 puntos. */
  overPoints?: true;
  /** Genesys: konamiIds de cartas prohibidas (Link/Péndulo). */
  forbiddenCards?: number[];
  /** Genesys: el torneo es anterior a la primera lista de puntos. */
  pointsBeforeHistory?: true;
};

export type TournamentDecklist = {
  id: number;
  source: 'YGOPRODECK';
  sourceId: string;
  sourceUrl: string | null;
  name: string;
  tournamentName: string;
  tournamentDate: string;
  placement: string;
  placementRank: number;
  playerCount: number | null;
  playerCountApproximate: boolean;
  playerName: string | null;
  banlistId: number | null;
  formatId: number;
  /** Sólo Genesys. */
  genesysPoints: number | null;
  metaDeckId: number | null;
  metaDeckName: string | null;
  matchConfidence: DecklistMatchConfidence | null;
  matchSource: 'AUTO' | 'MANUAL';
  isFeatured: boolean;
  isLegal: boolean;
  issues: TournamentDecklistIssues | null;
  decks: {
    main: TournamentDecklistCardEntry[];
    extra: TournamentDecklistCardEntry[];
    side: TournamentDecklistCardEntry[];
  };
};

export type MetaDeckDecklists = { example: TournamentDecklist | null; decklists: TournamentDecklist[] };

export type BanlistDecklistCount = { banlistId: number; name: string; effectiveDate: string; count: number };

export type GenesysListDecklistCount = { listSlug: string; name: string; effectiveDate: string; count: number };

/** Exactamente uno: la banlist (Advanced) o la lista de puntos (Genesys). Un número suelto es banlistId. */
export type MetaDeckDecklistsPeriod = number | { banlistId: number } | { genesysListSlug: string };

const TOURNAMENT_DECKLIST_FRAGMENT = gql`
  fragment TournamentDecklistFields on TournamentDecklist {
    id
    source
    sourceId
    sourceUrl
    name
    tournamentName
    tournamentDate
    placement
    placementRank
    playerCount
    playerCountApproximate
    playerName
    banlistId
    formatId
    genesysPoints
    metaDeckId
    metaDeckName
    matchConfidence
    matchSource
    isFeatured
    isLegal
    issues
    decks {
      main { code name quantity }
      extra { code name quantity }
      side { code name quantity }
    }
  }
`;

export const getMetaDeckDecklists = async (
  user: Nullable<UserWithToken>,
  metaDeckId: number,
  period: MetaDeckDecklistsPeriod
) =>
  getGraphQLQueryClient(user).request<WithData<MetaDeckDecklists>>(
    gql`
      ${TOURNAMENT_DECKLIST_FRAGMENT}
      query metaDeckDecklists($metaDeckId: Int!, $banlistId: Int, $genesysListSlug: String) {
        data: metaDeckDecklists(metaDeckId: $metaDeckId, banlistId: $banlistId, genesysListSlug: $genesysListSlug) {
          example { ...TournamentDecklistFields }
          decklists { ...TournamentDecklistFields }
        }
      }
    `,
    { metaDeckId, ...(typeof period === 'number' ? { banlistId: period } : period) }
  );

export const getMetaDeckDecklistBanlists = async (user: Nullable<UserWithToken>, metaDeckId: number) =>
  getGraphQLQueryClient(user).request<WithData<BanlistDecklistCount[]>>(
    gql`
      query metaDeckDecklistBanlists($metaDeckId: Int!) {
        data: metaDeckDecklistBanlists(metaDeckId: $metaDeckId) {
          banlistId
          name
          effectiveDate
          count
        }
      }
    `,
    { metaDeckId }
  );

export const getMetaDeckDecklistGenesysLists = async (user: Nullable<UserWithToken>, metaDeckId: number) =>
  getGraphQLQueryClient(user).request<WithData<GenesysListDecklistCount[]>>(
    gql`
      query metaDeckDecklistGenesysLists($metaDeckId: Int!) {
        data: metaDeckDecklistGenesysLists(metaDeckId: $metaDeckId) {
          listSlug
          name
          effectiveDate
          count
        }
      }
    `,
    { metaDeckId }
  );

export const getUnmatchedTournamentDecklists = async (
  user: Nullable<UserWithToken>,
  filters: { banlistId?: number; page?: number; limit?: number } = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<TournamentDecklist>>(
    gql`
      ${TOURNAMENT_DECKLIST_FRAGMENT}
      query unmatchedTournamentDecklists($filters: UnmatchedDecklistFilters) {
        data: unmatchedTournamentDecklists(filters: $filters) {
          data { ...TournamentDecklistFields }
          meta { total page perPage lastPage }
        }
      }
    `,
    { filters }
  );

export const setTournamentDecklistMetaDeck = async (
  user: Nullable<UserWithToken>,
  id: number,
  metaDeckId: number | null
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentDecklist>>(
    gql`
      ${TOURNAMENT_DECKLIST_FRAGMENT}
      mutation setTournamentDecklistMetaDeck($id: Int!, $metaDeckId: Int) {
        data: setTournamentDecklistMetaDeck(id: $id, metaDeckId: $metaDeckId) {
          ...TournamentDecklistFields
        }
      }
    `,
    { id, metaDeckId }
  );

export const setFeaturedTournamentDecklist = async (user: Nullable<UserWithToken>, id: number, featured: boolean) =>
  getGraphQLQueryClient(user).request<WithData<TournamentDecklist>>(
    gql`
      ${TOURNAMENT_DECKLIST_FRAGMENT}
      mutation setFeaturedTournamentDecklist($id: Int!, $featured: Boolean!) {
        data: setFeaturedTournamentDecklist(id: $id, featured: $featured) {
          ...TournamentDecklistFields
        }
      }
    `,
    { id, featured }
  );

export type CardUsageTier = 'CORE' | 'COMMON' | 'TECH';

export type DeckZone = 'MAIN' | 'EXTRA' | 'SIDE';

export type CardCopyCount = { copies: number; decks: number };

export type MetaDeckCardUsage = {
  code: number;
  name: string;
  deckType: DeckZone;
  deckCount: number;
  /** De 0 a 1. */
  share: number;
  minCopies: number;
  maxCopies: number;
  modeCopies: number;
  avgCopies: number;
  tier: CardUsageTier;
  copyCounts: CardCopyCount[];
  points: number | null;
};

export type DeckZoneSizes = { main: number; extra: number; side: number };

export type MetaDeckCardStats = {
  metaDeckId: number;
  metaDeckName: string | null;
  coverKonamiId: number | null;
  coverSquareUrl: string | null;
  formatId: number;
  banlistId: number | null;
  since: string | null;
  decklistCount: number;
  firstDate: string | null;
  lastDate: string | null;
  averageSize: DeckZoneSizes;
  averagePoints: number | null;
  cards: MetaDeckCardUsage[];
};

export type MetaDeckCardStatsFilters = {
  formatId?: number;
  banlistId?: number;
  /** ISO. */
  since?: string;
  /** 1 = sólo ganadores, 8 = hasta top 8… */
  maxPlacementRank?: number;
};

/** Pública. Sin filtros, la banlist más nueva con listas del mazo (Genesys: últimos 60 días). */
export const getPublicMetaDeckCardStats = async (
  user: Nullable<UserWithToken>,
  metaDeckId: number,
  filters: MetaDeckCardStatsFilters = {}
) =>
  getGraphQLQueryClient(user).request<WithData<MetaDeckCardStats>>(
    gql`
      query publicMetaDeckCardStats(
        $metaDeckId: Int!
        $formatId: Int
        $banlistId: Int
        $since: DateTime
        $maxPlacementRank: Int
      ) {
        data: publicMetaDeckCardStats(
          metaDeckId: $metaDeckId
          formatId: $formatId
          banlistId: $banlistId
          since: $since
          maxPlacementRank: $maxPlacementRank
        ) {
          metaDeckId
          metaDeckName
          coverKonamiId
          coverSquareUrl
          formatId
          banlistId
          since
          decklistCount
          firstDate
          lastDate
          averageSize { main extra side }
          averagePoints
          cards {
            code
            name
            deckType
            deckCount
            share
            minCopies
            maxCopies
            modeCopies
            avgCopies
            tier
            copyCounts { copies decks }
            points
          }
        }
      }
    `,
    { metaDeckId, ...filters }
  );

/** Pública: las banlists con listas legales del mazo, de la más nueva a la más vieja (selector de la ficha). */
export const getPublicMetaDeckDecklistBanlists = async (user: Nullable<UserWithToken>, metaDeckId: number) =>
  getGraphQLQueryClient(user).request<WithData<BanlistDecklistCount[]>>(
    gql`
      query publicMetaDeckDecklistBanlists($metaDeckId: Int!) {
        data: publicMetaDeckDecklistBanlists(metaDeckId: $metaDeckId) {
          banlistId
          name
          effectiveDate
          count
        }
      }
    `,
    { metaDeckId }
  );
